import type { OAuthContext } from '@common/api/authService';
import {
    getLocalStorageJson,
    removeLocalStorageItem,
    setLocalStorageJson
} from '@common/utils/storage';
import { useCallback, useEffect } from 'react';

const OAUTH_CONTEXT_KEY = 'bustly_oauth_context';

const parseOAuthContextFromSearch = (search: string): OAuthContext | null => {
    const searchParams = new URLSearchParams(search);
    const clientId = searchParams.get('client_id');
    const redirectUri = searchParams.get('redirect_uri');
    const fallbackRedirectUri = searchParams.get('fallback_redirect_uri');
    const deviceId = searchParams.get('device_id');
    const loginTraceId = searchParams.get('login_trace_id');

    if (!clientId || !loginTraceId) {
        return null;
    }

    return {
        client_id: clientId,
        redirect_uri: redirectUri,
        fallback_redirect_uri: fallbackRedirectUri,
        device_id: deviceId,
        login_trace_id: loginTraceId
    };
};

export const useOauth = () => {
    const storeOAuthContextFromUrl = useCallback((search?: string): OAuthContext | null => {
        if (typeof window === 'undefined' && search == null) return null;

        const oauthContext = parseOAuthContextFromSearch(search ?? window.location.search);
        if (!oauthContext) {
            return null;
        }

        setLocalStorageJson(OAUTH_CONTEXT_KEY, oauthContext);
        return oauthContext;
    }, []);

    const getOAuthContext = useCallback((): OAuthContext | null => {
        return getLocalStorageJson<OAuthContext>(OAUTH_CONTEXT_KEY);
    }, []);

    const getOAuthContextFromSearch = useCallback((search?: string): OAuthContext | null => {
        if (typeof window === 'undefined' && search == null) return null;
        return parseOAuthContextFromSearch(search ?? window.location.search);
    }, []);

    const clearOAuthContext = useCallback((): void => {
        removeLocalStorageItem(OAUTH_CONTEXT_KEY);
    }, []);

    // 直接从当前 URL 获取 OAuth 参数
    useEffect(() => {
        storeOAuthContextFromUrl();
    }, [storeOAuthContextFromUrl]);

    return {
        getOAuthContext,
        getOAuthContextFromSearch,
        clearOAuthContext,
        OAUTH_CONTEXT_KEY
    };
};
